import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Card, Button, Form, Alert, Table, Badge } from 'react-bootstrap';
import SimpleControlGestionLayout from '../../components/SimpleControlGestionLayout';
import { useGuias } from '../../../Mantenedores/hooks/useGuias';
import * as FaIcons from 'react-icons/fa';

const ControlDiario = () => {
  const { user: currentUser } = useSelector(state => state.auth);
  const {
    loading,
    error,
    success,
    dailyReception,
    estadisticas,
    handleGetDailyReception
  } = useGuias();

  const [filters, setFilters] = useState({
    fecha_inicio: new Date().toISOString().split('T')[0], // Fecha de hoy por defecto
    fecha_fin: '',
    subestado: '',
    estado: '',
    detalles: true
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Cargar datos iniciales con la fecha de hoy
    handleGetDailyReception(filters);
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    handleGetDailyReception(filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      fecha_inicio: new Date().toISOString().split('T')[0],
      fecha_fin: '',
      subestado: '',
      estado: '',
      detalles: true
    };
    setFilters(defaultFilters);
    handleGetDailyReception(defaultFilters);
  };

  const getEstadoColor = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'delivered': return 'success';
      case 'not_delivered': return 'danger';
      case 'in_transit': return 'warning';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  return (
    <SimpleControlGestionLayout
      title="Control Diario"
      subtitle="Visualiza y gestiona el control diario de operaciones y recepciones"
      icon={<FaIcons.FaCalendarDay />}
      iconColor="#28a745"
      breadcrumbs={[
        { label: 'Control Diario' }
      ]}
    >
      {/* Filtros */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaIcons.FaFilter className="me-2" />
              Filtros de Búsqueda
            </h5>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? <FaIcons.FaChevronUp /> : <FaIcons.FaChevronDown />}
            </Button>
          </div>
        </Card.Header>

        {showFilters && (
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Fecha Inicio *</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.fecha_inicio}
                    onChange={(e) => handleFilterChange('fecha_inicio', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Fecha Fin</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.fecha_fin}
                    onChange={(e) => handleFilterChange('fecha_fin', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    value={filters.estado}
                    onChange={(e) => handleFilterChange('estado', e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="delivered">Entregado</option>
                    <option value="not_delivered">No Entregado</option>
                    <option value="in_transit">En Tránsito</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Subestado</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Buscar subestado..."
                    value={filters.subestado}
                    onChange={(e) => handleFilterChange('subestado', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Detalles</Form.Label>
                  <Form.Check
                    type="checkbox"
                    label="Mostrar guías"
                    checked={filters.detalles}
                    onChange={(e) => handleFilterChange('detalles', e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex gap-2 mt-3">
              <Button
                variant="primary"
                onClick={handleApplyFilters}
                disabled={loading}
              >
                {loading ? <FaIcons.FaSpinner className="fa-spin me-2" /> : <FaIcons.FaSearch className="me-2" />}
                Aplicar Filtros
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleClearFilters}
                disabled={loading}
              >
                <FaIcons.FaTimes className="me-2" />
                Limpiar
              </Button>
            </div>
          </Card.Body>
        )}
      </Card>

      {/* Mensajes de estado */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <FaIcons.FaExclamationTriangle className="me-2" />
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4">
          <FaIcons.FaCheckCircle className="me-2" />
          {success}
        </Alert>
      )}

      {/* Estadísticas */}
      {estadisticas && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FaIcons.FaCalendarAlt size={30} className="text-primary mb-2" />
                <h4>{estadisticas.total_dias || 0}</h4>
                <small className="text-muted">Días</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FaIcons.FaTruck size={30} className="text-success mb-2" />
                <h4>{estadisticas.total_guias || 0}</h4>
                <small className="text-muted">Total Guías</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FaIcons.FaClock size={30} className="text-warning mb-2" />
                <h4>{estadisticas.modo_recepcionado ? 'Sí' : 'No'}</h4>
                <small className="text-muted">Modo Recepcionado</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FaIcons.FaCalendarCheck size={30} className="text-info mb-2" />
                <h4>{formatDate(estadisticas.fecha_inicio)}</h4>
                <small className="text-muted">Fecha Inicio</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Resultados */}
      {loading ? (
        <div className="text-center py-5">
          <FaIcons.FaSpinner className="fa-spin" size={40} />
          <p className="mt-3">Cargando datos...</p>
        </div>
      ) : (
        dailyReception && dailyReception.length > 0 ? (
          dailyReception.map((dia, index) => (
            <Card key={index} className="mb-4">
              <Card.Header>
                <h5 className="mb-0">
                  <FaIcons.FaCalendarDay className="me-2" />
                  {formatDate(dia.fecha)}
                  <Badge bg="secondary" className="ms-2">
                    {dia.total} guías
                  </Badge>
                </h5>
              </Card.Header>
              <Card.Body>
                {dia.subestados && dia.subestados.map((subestado, subIndex) => (
                  <div key={subIndex} className="mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <Badge bg={getEstadoColor(subestado.estado)} className="me-2">
                        {subestado.estado}
                      </Badge>
                      <strong>{subestado.subestado}</strong>
                      <Badge bg="light" text="dark" className="ms-2">
                        {subestado.cantidad} guías
                      </Badge>
                    </div>

                    {filters.detalles && subestado.guias && subestado.guias.length > 0 && (
                      <Table striped bordered hover size="sm" className="mt-2">
                        <thead>
                          <tr>
                            <th>Guía</th>
                            <th>Cliente</th>
                            <th>Destinatario</th>
                            <th>Ruta</th>
                            <th>Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subestado.guias.map((guia, guiaIndex) => (
                            <tr key={guiaIndex}>
                              <td>{guia.guia}</td>
                              <td>{guia.cliente?.cliente}</td>
                              <td>{guia.destinatario?.nombre}</td>
                              <td>{guia.ruta_actual?.ruta}</td>
                              <td>
                                <Badge bg={getEstadoColor(guia.ultimo_estado?.estado)}>
                                  {guia.ultimo_estado?.sub_estado}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                ))}
              </Card.Body>
            </Card>
          ))
        ) : (
          <div className="text-center py-5">
            <FaIcons.FaInbox size={40} className="text-muted mb-3" />
            <h5>No se encontraron resultados</h5>
            <p className="text-muted">Intenta ajustar los filtros de búsqueda</p>
          </div>
        )
      )}
    </SimpleControlGestionLayout>
  );
};

export default ControlDiario;
